import MP4Box from "mp4box"

const validSrtTrackCodecs = new Set(["text", "tx3g", "mov_text"])

function srtTimecode(seconds) {
    const ms = Math.round(seconds * 1000)
    const pad = (value, length) => String(value).padStart(length, "0")
    return `${pad(Math.floor(ms / 3600000), 2)}:${pad(Math.floor(ms / 60000) % 60, 2)}:${pad(
        Math.floor(ms / 1000) % 60,
        2
    )},${pad(ms % 1000, 3)}`
}

class MP4FileSink {
    #setStatus = null
    #file = null
    #offset = 0

    constructor(file, setStatus) {
        this.#file = file
        this.#setStatus = setStatus
    }

    write(chunk) {
        chunk.fileStart = this.#offset
        this.#offset += chunk.byteLength
        this.#setStatus("fetch", (this.#offset / 1024 ** 2).toFixed(1) + " MiB")
        this.#file.appendBuffer(chunk)
    }

    close() {
        this.#setStatus("fetch", "Done")
        this.#file.flush()
    }
}

export class MP4Demuxer {
    #onConfig = null
    #onChunk = null
    #setStatus = null
    #file = null
    #onEnd = null
    #setDownloadProgress = null
    framesInProgress = null

    #source = null
    #isUrl = false
    #signedUrl = null
    #streamGeneration = 0

    constructor(
        source,
        { onConfig, onChunk, setStatus, onEnd, framesInProgress, srt, setDownloadProgress }
    ) {
        this.#onConfig = onConfig
        this.#onChunk = onChunk
        this.#setStatus = setStatus
        this.#onEnd = onEnd
        this.#setDownloadProgress = setDownloadProgress
        this.framesInProgress = framesInProgress
        this.srt = srt
        this.#source = source
        this.#isUrl = typeof source === "string"

        this.#file = MP4Box.createFile(false)
        this.#file.onError = (error) => setStatus("demux", error)
        this.#file.onReady = this.#onReady.bind(this)
        this.#file.onSamples = this.#onSamples.bind(this)

        if (this.#isUrl) {
            this.#loadFromS3(source)
        } else {
            this.file = source
            this.#pumpStream(source.stream())
        }
    }

    async #loadFromS3(videoUrl) {
        try {
            this.#signedUrl = videoUrl

            const response = await fetch(videoUrl, { credentials: "include" })
            const contentLength = response.headers.get("Content-Length")

            this.#pumpStream(response.body, contentLength ? parseInt(contentLength) : 0)
        } catch (error) {
            this.#setStatus("demux", `Failed to fetch video file: ${error.message}`)
        }
    }

    async #pumpStream(readableStream, contentLength = 0) {
        const currentGeneration = this.#streamGeneration

        const fileSink = new MP4FileSink(this.#file, this.#setStatus)
        const reader = readableStream.getReader()
        this.reader = reader

        const writeStream = new WritableStream(fileSink, {
            highWaterMark: 2,
        }).getWriter()

        let offset = 0
        const setDownloadProgress = this.#setDownloadProgress
        const framesInProgress = this.framesInProgress

        reader.read().then(
            async function getNextChunk({ done, value }) {
                if (currentGeneration !== this.#streamGeneration) {
                    reader.cancel().catch(() => {})
                    return
                }

                if (done) return

                if (framesInProgress && framesInProgress.promise) {
                    await framesInProgress.promise
                }

                const copy = value.buffer
                copy.fileStart = offset
                offset += value.length

                if (contentLength && setDownloadProgress) {
                    setDownloadProgress(offset / contentLength)
                }

                await writeStream.write(copy)

                if (currentGeneration !== this.#streamGeneration) {
                    reader.cancel().catch(() => {})
                    return
                }

                return reader.read().then(getNextChunk.bind(this))
            }.bind(this)
        )
    }

    #description(track) {
        const trak = this.#file.getTrackById(track.id)
        for (const entry of trak.mdia.minf.stbl.stsd.entries) {
            const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C
            if (box) {
                const stream = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN)
                box.write(stream)
                return new Uint8Array(stream.buffer, 8)
            }
        }
        throw new Error("avcC, hvcC, vpcC, or av1C box not found")
    }

    getValidSrtTrack(track) {
        if (track && validSrtTrackCodecs.has(track.codec)) return track
    }

    async #onReady(info) {
        this.#streamGeneration++

        if (this.reader) this.reader.cancel().catch(() => {})

        const track = this.srt
            ? [...info.otherTracks, ...info.subtitleTracks]
                  .map((t) => this.getValidSrtTrack(t))
                  .find(Boolean)
            : info.videoTracks[0]

        if (!track) return this.#onEnd(null)

        this.duration = track.duration / track.timescale
        if (!this.duration) return this.#onEnd(null)

        this.sampleNumber = 0
        this.numSamples = track.nb_samples
        this.track = track

        if (!this.srt) {
            this.#onConfig({
                codec: track.codec.startsWith("vp08") ? "vp8" : track.codec,
                codedHeight: track.video.height,
                codedWidth: track.video.width,
                description: this.#description(track),
                duration: this.duration,
                numFrames: track.nb_samples, // <--- Passes total frame count here
            })
        }

        if (this.#isUrl) {
            const response = await fetch(this.#signedUrl, { credentials: "include" })
            const contentLength = response.headers.get("Content-Length")
            this.#pumpStream(response.body, contentLength ? parseInt(contentLength) : 0)
        } else {
            this.#pumpStream(this.file.stream())
        }

        this.#file.setExtractionOptions(track.id, this.srt ? "srt" : "video")
        this.#file.start()
    }

    #onSamples(track_id, ref, samples) {
        if (this.done) return

        if (ref === "srt") {
            this.cueIndex ??= 1
            for (const sample of samples) {
                this.sampleNumber = Math.max(this.sampleNumber, sample.number)
                const start = srtTimecode(sample.cts / sample.timescale)
                const end = srtTimecode((sample.cts + sample.duration) / sample.timescale)
                const payload = new TextDecoder().decode(sample.data.subarray(2))
                this.#onChunk(`${this.cueIndex++}\n${start} --> ${end}\n${payload}\n\n`)
            }
            if (Math.abs(this.sampleNumber - this.numSamples) <= 1) {
                this.done = true
                this.#onEnd()
            }
            this.#file.releaseUsedSamples(track_id, samples[samples.length - 1].number)
            return
        }

        const framesInProgress = this.framesInProgress
        framesInProgress.count += samples.length
        if (framesInProgress.count > 1000) {
            const promise = new Promise((resolve) => {
                framesInProgress.resolve = resolve
            })
            framesInProgress.promise = promise
        }

        for (const sample of samples) {
            this.sampleNumber = Math.max(this.sampleNumber, sample.number)
            this.#onChunk(
                new EncodedVideoChunk({
                    type: sample.is_sync ? "key" : "delta",
                    timestamp: (1e6 * sample.cts) / sample.timescale,
                    duration: (1e6 * sample.duration) / sample.timescale,
                    data: sample.data,
                }),
                this.sampleNumber / this.numSamples
            )
        }

        if (Math.abs(this.sampleNumber - this.numSamples) <= 1) {
            this.#onEnd()
            this.done = true
        }
        this.#file.releaseUsedSamples(track_id, samples[samples.length - 1].number)
    }
}
